// Payment-card field validation + formatting helpers.
// Extracted from PaymentPage.js so the page component stays presentation-only
// and these rules are unit-testable in isolation.

/** Pretty-prints a card number as groups of 4, max 19 chars (16 digits + spaces). */
export function formatCardNumber(value) {
  const cleaned = (value || '').replace(/\D/g, '');
  const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
  return formatted.substring(0, 19);
}

/** Luhn checksum — accepts spaces; returns true for 13–19 digit cards. */
export function validateCardNumber(cardNumber) {
  const cleaned = (cardNumber || '').replace(/\s/g, '');
  if (cleaned.length < 13 || cleaned.length > 19) return false;

  let sum = 0;
  let isEven = false;
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
}

/** Expiry is in the future (or current month/year). */
export function validateExpiryDate(month, year) {
  const now = new Date();
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);
  if (Number.isNaN(monthNum) || Number.isNaN(yearNum)) return false;
  if (monthNum < 1 || monthNum > 12) return false;
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (yearNum < currentYear) return false;
  if (yearNum === currentYear && monthNum < currentMonth) return false;
  return true;
}

/**
 * Sanitise a single field's value as the user types.
 *   - card_number → grouped digits
 *   - expiry_month → 2 digits
 *   - expiry_year → 4 digits
 *   - cvv → 4 digits
 *   - cardholder_name → uppercased
 */
export function sanitiseCardField(field, value) {
  switch (field) {
    case 'card_number':
      return formatCardNumber(value);
    case 'expiry_month':
      return (value || '').replace(/\D/g, '').substring(0, 2);
    case 'expiry_year':
      return (value || '').replace(/\D/g, '').substring(0, 4);
    case 'cvv':
      return (value || '').replace(/\D/g, '').substring(0, 4);
    case 'cardholder_name':
      return (value || '').toUpperCase();
    default:
      return value;
  }
}

/** Returns an `errors` map; empty when the form is valid. */
export function validateCardForm(card) {
  const errors = {};
  if (!card.cardholder_name?.trim()) {
    errors.cardholder_name = 'Cardholder name is required';
  }
  if (!validateCardNumber(card.card_number)) {
    errors.card_number = 'Invalid card number';
  }
  if (!card.expiry_month || !card.expiry_year) {
    errors.expiry = 'Expiry date is required';
  } else if (!validateExpiryDate(card.expiry_month, card.expiry_year)) {
    errors.expiry = 'Card has expired or invalid date';
  }
  if (!card.cvv || card.cvv.length < 3) {
    errors.cvv = 'Invalid CVV';
  }
  return errors;
}

export const TEST_CARD = {
  card_number: '4444 5555 6666 7777',
  cardholder_name: 'TEST CARD',
  expiry_month: '01',
  expiry_year: '2035',
  cvv: '555',
};
